import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { LinearGradient } from 'expo-linear-gradient';
import { colors, controleLarge, formes, gradients, radius, spacing, typography } from '../theme/tokens';
import { fleche } from '../i18n/sens';

export interface GradientButtonProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  loading?: boolean;
  withArrow?: boolean;
  icon?: React.ReactNode; // icône optionnelle à gauche du label (ex. caméra)
  // 'marque' : dégradé des écrans actuels. 'cta' : dégradé des écrans refaits (électrique, violet, corail).
  variante?: 'marque' | 'cta';
  // Hauteur de la maquette : 52, 56 ou 58. Sans elle, 52 comme avant.
  hauteur?: 'md' | 'lg' | 'xl';
  testID?: string;
}

// Bouton au dégradé officiel charte (cyan → corail), texte blanc, coins très arrondis.
// Désactivé → fond plat neutre (pas de dégradé délavé), comme Button.
export function GradientButton({
  label,
  onPress,
  disabled = false,
  loading = false,
  withArrow = false,
  icon,
  variante = 'marque',
  hauteur,
  testID,
}: GradientButtonProps) {
  const inactive = disabled || loading;
  const showDisabled = disabled && !loading;
  const cta = variante === 'cta';
  const hauteurFond = hauteur ? formes.hauteur[hauteur] : HAUTEUR_PAR_DEFAUT;

  const inner = loading ? (
    <ActivityIndicator testID={testID ? `${testID}-spinner` : undefined} color={colors.white} />
  ) : (
    <>
      {icon && !showDisabled ? <View style={styles.icon}>{icon}</View> : null}
      {/* Le libellé et la flèche sont des nœuds Text DISTINCTS. Fondus dans un seul nœud, le
          contenu devenait « Créer mon compte  → » et aucun getByText('Créer mon compte') ne le
          trouvait : les libellés des boutons dégradés — tous les appels à l'action principaux de
          l'application — échappaient donc aux tests, sans que rien ne le signale. */}
      <Text style={[styles.label, cta && styles.labelCta, showDisabled && styles.labelDisabled]}>{label}</Text>
      {withArrow ? (
        // La marge sur une View, pas sur le texte : sur le web, un texte reçoit dir="auto" et une flèche
        // seule y est lue de gauche à droite, sa marge de début tomberait du mauvais côté en arabe.
        <View style={styles.arrow}>
          <Text style={[styles.label, cta && styles.labelCta, showDisabled && styles.labelDisabled]}>{fleche('→')}</Text>
        </View>
      ) : null}
    </>
  );

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ disabled: inactive, busy: loading }}
      disabled={inactive}
      onPress={onPress}
      style={controleLarge}
    >
      {showDisabled ? (
        <View style={[styles.fill, { height: hauteurFond }, styles.disabledFill]}>{inner}</View>
      ) : (
        <LinearGradient
          colors={cta ? gradients.cta : gradients.brand}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[styles.fill, { height: hauteurFond }, loading && styles.loadingFill]}
        >
          {inner}
        </LinearGradient>
      )}
    </Pressable>
  );
}

// 52 en dur et non `formes.hauteur.md` : ce dernier est échelonné sur grand écran, et les boutons
// actuels ne l'étaient pas. Les écrans refaits qui passent `hauteur` choisissent l'échelonné.
const HAUTEUR_PAR_DEFAUT = 52;

const styles = StyleSheet.create({
  fill: {
    borderRadius: radius.pill, // coins très arrondis (charte)
    paddingHorizontal: spacing.xl,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledFill: { backgroundColor: colors.disabledBg },
  loadingFill: { opacity: 0.6 },
  icon: { marginEnd: spacing.sm },
  label: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.white },
  // Les boutons d'action refaits écrivent leur libellé en 17 pt très gras (maquettes).
  labelCta: { fontSize: 17, fontWeight: '800' },
  arrow: { marginStart: spacing.sm },
  labelDisabled: { color: colors.disabledText },
});
